import React, { useMemo } from 'react';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { BarChart } from '@mui/x-charts';
import { LenderRetailActivityDeclineReasonRowViewModel } from '../../../api/types';
import { mapDeclineReasonDistribution } from '../../../lib';

interface Props {
  rows: LenderRetailActivityDeclineReasonRowViewModel[];
  loading?: boolean;
}

const CHART_COLOR = '#FFAB91';
const CHART_MARGIN_LEFT = 200;
// Rough average glyph width (px) for the bar label font, used to decide if a label fits inside its bar
const BAR_LABEL_CHAR_WIDTH = 7;
const BAR_LABEL_PADDING = 8;
// Rough average glyph width (px) for the y-axis label font
const AXIS_LABEL_CHAR_WIDTH = 6.5;
// Space reserved for tick marks/padding between the label and the chart area
const AXIS_LABEL_PADDING = 12;

const getBarLabelForWidth = (value: number | null, barWidth: number): string => {
  if (value == null) return '';

  const text = `${Math.round(value)}%`;
  const estimatedTextWidth = text.length * BAR_LABEL_CHAR_WIDTH + BAR_LABEL_PADDING;

  // Hide the label when the bar is too small to fit it, so it doesn't overflow into the axis and get clipped
  return estimatedTextWidth <= barWidth ? text : '';
};

// Truncate the reason label from the end (keeping the start visible) so it fits within the left margin
const truncateLabel = (label: string): string => {
  const maxWidth = CHART_MARGIN_LEFT - AXIS_LABEL_PADDING;
  const maxChars = Math.floor(maxWidth / AXIS_LABEL_CHAR_WIDTH);

  if (label.length <= maxChars) return label;
  if (maxChars <= 1) return '…';

  return `${label.slice(0, maxChars - 1).trimEnd()}…`;
};

export const DeclineReasonBreakdown = ({ rows, loading }: Props) => {
  const distribution = useMemo(() => mapDeclineReasonDistribution(rows), [rows]);
  const chartHeight = Math.max(distribution.length * 40 + 40, 120);

  return (
    <Stack gap={1}>
      <Typography variant="subtitle2" component="span">
        Declines — Reason breakdown
      </Typography>

      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
        {loading ? (
          <Skeleton variant="rounded" height={chartHeight} />
        ) : !distribution.length ? (
          <Typography sx={{ mt: 2 }} color="text.secondary">
            No data
          </Typography>
        ) : (
          <BarChart
            layout="horizontal"
            height={chartHeight}
            margin={{ left: CHART_MARGIN_LEFT, top: 10, right: 40, bottom: 24 }}
            yAxis={[
              {
                scaleType: 'band',
                // Key by index so duplicate reason labels don't collapse onto the same band
                data: distribution.map((_, index) => index),
                valueFormatter: (value, context) => {
                  const label = distribution[Number(value)]?.label ?? '';
                  return context.location === 'tick' ? truncateLabel(label) : label;
                },
              },
            ]}
            xAxis={[{ min: 0, max: 100, valueFormatter: value => `${value}%` }]}
            series={[
              {
                data: distribution.map(item => item.percentage),
                color: CHART_COLOR,
                valueFormatter: value => (value == null ? '' : `${Math.round(value)}%`),
              },
            ]}
            barLabel={(item, context) => getBarLabelForWidth(item.value, context.bar.width)}
            slotProps={{ legend: { hidden: true } }}
          />
        )}
      </Box>
    </Stack>
  );
};
